import { getColumns } from './EndpointFilterBuilder.Grid';
import BespokeConditionBuilder from '../../Functions/Bespoke/BespokeConditionBuilder';
import ConditionBuilder from '../../Functions/Condition/ConditionBuilder';
import conditionDisplayHelper from '../../../infrastructure/helpers/functions/condition/conditionDisplayHelper';
import EndpointFilter from '../../../types/report/EndpointFilter';
import ErrorList from '../../Common/ErrorList/ErrorList';
import React from 'react';
import reportConfigNavigation from '../../../infrastructure/helpers/report/navigation/reportConfigNavigation';
import ReportLogicList from '../Common/ReportLogicList/ReportLogicList';
import ReportLogicLocation from '../../../types/functions/Location/ReportLogicLocation';
import StatementBuilderProps from '../../Functions/Statement/StatementBuilderProps';
import Condition from '../../../types/functions/Condition/Condition';
import assignmentHelper from '../../../infrastructure/helpers/functions/assignment/assignmentHelper';
import reorderHelper from '../../../infrastructure/helpers/common/reorderHelper';
import ComponentType from '../../../types/report/ComponentType';
import DragAndDropItem from '../../../types/report/DragAndDropItem';
import reportComponentBuildersHelper from '../Common/ReportComponentsHelper';
import copyArray from '../../../infrastructure/helpers/common/copyArray';
import { endpointFilterType } from '../../../infrastructure/constants/report/endpointFilterType';
import { reportType } from '../../../infrastructure/constants/reportType';
import InsertAtPositionModal from '../../Common/Modals/InsertAtPositionModal/InsertAtPositionModal';
import { SelectSearchOption } from 'react-select-search';
import IdNameAndNumber from '../../../types/report/IdNameAndNumber';
import ActionType from '../../../types/report/ActionType';
import sortingHelper from '../../../infrastructure/helpers/common/sortingHelper';

class EndpointFilterBuilder extends React.Component<StatementBuilderProps<EndpointFilter>, { showPopup: boolean }> {
    constructor(props: StatementBuilderProps<EndpointFilter>) {
        super(props);

        this.state = {
            showPopup: false
        }

        this.onNameChange = this.onNameChange.bind(this);
        this.onBusinessDescriptionChange = this.onBusinessDescriptionChange.bind(this);
        this.onBespokeToggleClick = this.onBespokeToggleClick.bind(this);
        this.getConditionContent = this.getConditionContent.bind(this);
        this.reorder = this.reorder.bind(this);
        this.onDropBetweenFilters = this.onDropBetweenFilters.bind(this);
        this.onFilterConfiguredInPopup = this.onFilterConfiguredInPopup.bind(this);
        this.onAddFilterClick = this.onAddFilterClick.bind(this);
        this.validateInsertBeforeAfter = this.validateInsertBeforeAfter.bind(this);
    }

    onNameChange(name: string, number: number): void {

        this.props.onStatementChange({ number: number, name: name } as EndpointFilter);
    }

    onBusinessDescriptionChange(businessDescription: string, number: number): void {

        this.props.onStatementChange({ number: number, businessDescription: businessDescription } as EndpointFilter);
    }

    onBespokeToggleClick(isBespoke: boolean, filter: EndpointFilter): void {

        let changes = { number: filter.number, isBespoke: isBespoke } as EndpointFilter;

        if (isBespoke) {
            changes.bespokeCondition = conditionDisplayHelper.buildConditions(filter.condition, [], [], [], [], this.props.collections.reportFields, null as any, this.props.reportConfig.type);
        }

        this.props.onStatementChange(changes);
    }

    onReorderConditions(condition: Condition, filter: EndpointFilter) {

        filter.condition = condition;
        this.props.onStatementChange(filter);
    }

    async onClearClick(condition: Condition, filter: EndpointFilter): Promise<void> {

        let newFilter = assignmentHelper.clearOperandsInStatementByConditionNumber(condition.number, filter) as EndpointFilter;

        this.props.onStatementChange(newFilter);
    }

    clearAllErrors() {
        this.props.reportConfig.endpointFilters.forEach((filter) => {
            filter.errors = [];
        });
    }

    async reorder(dragNumber: number, dropNumber: number): Promise<void> {

        let dragIndex = this.props.statements.findIndex(x => x.number === dragNumber);
        let dropIndex = this.props.statements.findIndex(x => x.number === dropNumber);

        let draggedFilter = this.props.statements.find(v => v.number === dragNumber) as EndpointFilter;
        let dropFilter = this.props.statements.find(v => v.number === dropNumber) as EndpointFilter;

        this.clearAllErrors();

        let result = reportComponentBuildersHelper.validateDragAndDrop(draggedFilter as DragAndDropItem, dropFilter as DragAndDropItem, ComponentType.EndpointFilter);

        if (result.error) {
            const filterReferenced = this.props.statements.find(s => s.number === result.number) || {} as EndpointFilter;

            result.error = `${result.error} The referenced filter is "${filterReferenced.name}".`;

            const number = result.isDrag ? draggedFilter.number : dropFilter.number;

            const statement = this.props.statements.find(s => s.number === number) || {} as EndpointFilter;

            statement.errors = [result.error];

            this.props.onStatementChange(statement);

            return;
        }
        else {

            reorderHelper.reorderWithNumbersChange(this.props.statements, dragIndex, dropIndex);

            let filterType = this.props.statements?.[0]?.type;

            let otherFilters = copyArray(this.props.reportConfig.endpointFilters).filter(x => x.type !== filterType);

            this.props.onDragAndDrop(this.props.statements.concat(otherFilters));
        }
    }

    onDropBetweenFilters(dragNumber: number, dropNumber: number) {

        let numbersMap = reorderHelper.prepareNewNumbersMapOnDragBetween(dragNumber, dropNumber);

        this.props.onDragStatementBetween && this.props.onDragStatementBetween(numbersMap);
    }

    getConditionContent(filter: EndpointFilter): JSX.Element {

        if (filter.isBespoke) {
            return (
                <BespokeConditionBuilder
                    keyword={this.props.details.keyword}
                    keyword2={this.props.details.keyword2}
                    statement={filter}
                    onChange={this.props.onStatementChange}
                    isReadOnly={this.props.isReadOnly} />
            );
        }

        return (
            <>
                <ConditionBuilder
                    reportConfigType={this.props.reportConfig.type}
                    isReadOnly={this.props.isReadOnly}
                    allowCopy={true}
                    keyword={this.props.details.keyword}
                    functions={this.props.reportConfig.userDefinedFunctions}
                    globalFunctions={this.props.collections.globalModuleConfig.userDefinedFunctions}
                    lookups={this.props.reportConfig.lookups}
                    dictionaries={this.props.reportConfig.dictionaries}
                    variables={this.props.reportConfig.variables}
                    dataSources={this.props.collections.dataSources}
                    reportFields={this.props.collections.reportFields}
                    customDataSourceFields={null as any}
                    location={{ statement: this.props.details.statement, statementNumber: filter.number } as ReportLogicLocation}
                    condition={filter.condition}
                    onLogicalOperatorChange={this.props.onLogicalOperatorChange}
                    onParenthesesChange={this.props.onParenthesesChange}
                    onAddConditionClick={this.props.onAddConditionClick}
                    onRemoveConditionClick={this.props.onRemoveConditionClick}
                    onComparisonTypeChange={this.props.onComparisonTypeChange}
                    onOperandClick={this.props.onOperandClick}
                    onCopyClick={this.props.onCopyClick}
                    onPasteClick={this.props.onPasteClick}
                    allowPaste={this.props.allowPaste}
                    onClearClick={async (e, condition) => { this.onClearClick(condition, filter); }}
                    onReorderConditions={(condition) => { this.onReorderConditions(condition, filter); }} />

                <div className="code-block">
                    <span className="code">{this.props.details.keyword2}</span>
                </div>

                <ErrorList errors={filter.errors} className="mt-2" />
            </>
        );
    }
    onAddFilterClick(): void {

        let filtersLength = this.props.reportConfig.endpointFilters.filter(x => x.type === this.props.statements?.[0]?.type).length;

        if (filtersLength > 0) {
            this.setState({ showPopup: true });
        }
        else {
            const name = `${this.props.details.name}-${filtersLength + 1}`;
            this.props.onAddStatementClick(name, () => { });
        }
    }

    async onFilterConfiguredInPopup(name: string, type: string, isInsertAfter: boolean, selectedNumber: string): Promise<void> {
        this.setState({ showPopup: false });
        const insertionAfterNumber: number = parseInt(selectedNumber);

        let filtersLength = this.props.reportConfig.endpointFilters.filter(x => x.type === this.props.statements[0].type).length;
        let finalName = name || `${this.props.details.name}-${filtersLength + 1}`;

        this.props.onAddStatementClick(finalName, (filter, filters) => {

            let newFilters = this.props.reportConfig.endpointFilters.filter(x => x.type === this.props.statements[0].type);

            newFilters = reorderHelper.rearangeElementsWhenAddNewOne(newFilters, filter, isInsertAfter, insertionAfterNumber) as EndpointFilter[];

            if (!name) {
                filter._internalId = filter.number;
            }
            else {
                filter.name = name
            }

            filters.forEach((f) => {
                if (f.type === this.props.statements[0].type) {
                    let el = newFilters.find(x => x.name === f.name);

                    f.number = el?.number as number;
                }
            });

            filters = sortingHelper.sortByNumberAsc(filters) as EndpointFilter[];
        });
    }

    prepareOptionsFromFilters(): SelectSearchOption[] {

        let options: SelectSearchOption[] = [];

        this.props.reportConfig.endpointFilters.filter(x => x.type === this.props.statements?.[0]?.type).forEach((filter) => {
            options.push({
                name: filter.name,
                value: filter.name,
                items: [{ name: filter.number.toString(), value: filter.number.toString() }]
            });
        });

        return options;
    }

    async validateInsertBeforeAfter(number: number, isInsertAfter: boolean) {

        return await reportComponentBuildersHelper.validateAction(
            number,
            isInsertAfter,
            this.props.reportConfig.endpointFilters.filter(x => x.type === this.props.statements?.[0]?.type) as IdNameAndNumber[],
            ComponentType.Filter,
            ActionType.Add,
            this.props.reportConfig);
    }

    render(): JSX.Element {

        const filterOptions = this.prepareOptionsFromFilters();

        let filterType = this.props.statements?.[0]?.type === endpointFilterType.regular ? ComponentType.EndpointFilter :
            this.props.statements?.[0]?.type === endpointFilterType.underReport ? ComponentType.UnderReportFilter :
                ComponentType.OverReportFilter;

        return (
            <>
                <ReportLogicList
                    onDropZoneDrop={this.onDropBetweenFilters}
                    isWaiting={this.props.isWaiting}
                    items={this.props.statements}
                    columns={getColumns(this.onNameChange,
                        this.onBusinessDescriptionChange,
                        this.onBespokeToggleClick,
                        this.props.onRemoveStatementClick,
                        this.reorder,
                        this.props.isReadOnly,
                        filterType,
                        this.props.reportConfig.id as number)}
                    getContent={this.getConditionContent}
                    getKey={f => f.number}
                    createHtmlId={reportConfigNavigation.buildEndpointFilterId} />
                {
                    !this.props.isReadOnly &&
                    !this.props.isWaiting &&
                    (this.props.reportConfig.type !== reportType.accuracy ||
                        (this.props.reportConfig.type === reportType.accuracy && this.props.reportConfig.filters.length === 0)) &&

                    <div className="mt-4 mb-4">
                        <button onClick={this.onAddFilterClick} className="btn btn-light">+ {this.props.details.name}</button>
                    </div>
                }
                {
                    this.state.showPopup ?
                        <InsertAtPositionModal
                            title='New Filter'
                            onSaveClick={this.onFilterConfiguredInPopup}
                            name={this.props.details.name}
                            dropdownOptions={filterOptions}
                            onClose={() => { this.setState({ showPopup: false }); }}
                            defaultDropdownOption={filterOptions[0]}
                            defaultDropdownOptionAlwaysLastOne={true}
                            validateFunction={this.validateInsertBeforeAfter}
                        />
                        : null
                }
            </>
        );
    }
}

export default EndpointFilterBuilder;
